import { forwardRef, useEffect, useRef } from 'react';
import type { InputHTMLAttributes } from 'react';

// Non-text-ish control — doesn't borrow Input.tsx's `inputClass` (background/
// border/padding tuned for a text box); invalid state gets its own minimal
// ring-only treatment instead.
const invalidRingClass = 'ring-2 ring-destructive';

type CheckboxSize = 'sm' | 'md';

const SIZE_CLASS: Record<CheckboxSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
};

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'checked' | 'size'> {
  /** Render-prop convention: boolean under `value`, not the native `checked`
   * name — so `<Checkbox {...field} />` spreads cleanly off FormField's
   * render props (whose value key is always `value`, whatever the control). */
  value?: boolean;
  invalid?: boolean;
  size?: CheckboxSize;
  /**
   * Tri-state support for parent rows in a nested tree (P10 §2.2's
   * `TeamTree` — region → branch → team — needs "indeterminate parents");
   * cheap to wire now, before that story needs it. `indeterminate` is a DOM
   * PROPERTY, not an HTML attribute — there is no JSX prop for it, so it has
   * to be set imperatively via a ref effect.
   */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { value, invalid, indeterminate = false, size = 'md', className = '', ...rest },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      // Merge FormField's forwarded ref (RHF's shouldFocusError needs it to
      // move focus here on a validation error) with the local ref this file
      // needs to imperatively flip `.indeterminate`.
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      aria-invalid={invalid || undefined}
      className={`focus-ring ${SIZE_CLASS[size]} rounded border-border accent-primary ${invalid ? invalidRingClass : ''} ${className}`}
      {...rest}
      type="checkbox"
      checked={Boolean(value)}
    />
  );
});
